import React, { useEffect, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Flag, RotateCcw,
} from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { fmtPercent, QuizVerdictBadge } from './quizFormat';

/**
 * The sitting screens shared by the real quiz (pages/QuizPage.js) and the
 * Preview (QuizPreviewModal): the question step, the review grid, the
 * watermark, the copy / shortcut guards and the focus-loss detector. Both
 * surfaces render these same pieces so a preview looks and behaves exactly
 * like the participant's paper.
 *
 * `questions`: [{ id, type, text, points, mustPass, options: [{ key, text }] }]
 * `answers`:   { [questionId]: [optionKey, ...] }
 */

// Blocked with Ctrl / ⌘ held: copy, cut, select all, print, save, view source,
// devtools shortcut. Letters only — arrows, tab and enter keep working.
const BLOCKED_KEYS = new Set(['c', 'x', 'a', 's', 'p', 'u', 'i', 'j']);

const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };

const guardProps = {
  onCopy: swallow, onCut: swallow, onPaste: swallow, onContextMenu: swallow, onDragStart: swallow,
};

/** Hands off the paper: no selecting, copying, dragging; prints blank. */
export const SecurePaper = ({ watermark, children }) => (
  <div className="quiz-secure select-none relative" {...guardProps} style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
    <style>{'@media print { .quiz-secure { display: none !important; } }'}</style>
    <Watermark text={watermark} />
    <div className="relative">{children}</div>
  </div>
);

const Watermark = ({ text }) => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden select-none">
    {Array.from({ length: 12 }).map((_, i) => (
      <span
        key={i}
        className="absolute text-[11px] font-medium text-slate-900/[0.045] whitespace-nowrap -rotate-[24deg]"
        style={{ top: `${(i % 6) * 18 + 4}%`, left: `${i < 6 ? 2 : 48}%` }}
      >
        {text} · {text}
      </span>
    ))}
  </div>
);

/**
 * Keyboard shortcuts for copy / print / save / source / devtools, and a
 * PrintScreen best-effort (clears the clipboard right after). With
 * `confirmLeave`, closing or reloading the page asks first.
 */
export const useKeyGuard = (active, { confirmLeave = false } = {}) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (e) => {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && BLOCKED_KEYS.has(k)) swallow(e);
      if (k === 'printscreen') swallow(e);
    };
    const onKeyUp = (e) => {
      if ((e.key || '').toLowerCase() === 'printscreen' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText('').catch(() => {});
      }
    };
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    if (confirmLeave) window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      if (confirmLeave) window.removeEventListener('beforeunload', onLeave);
    };
  }, [active, confirmLeave]);
};

/**
 * Focus loss: `onAway` once per absence (blur and visibilitychange both fire
 * on a tab switch), `onBack` once the page is both visible and focused again
 * — a window uncovered on screen but not clicked is still away. Returns the
 * ref holding "away right now", which the caller resets on a new sitting.
 */
export const useFocusLoss = (active, { onAway, onBack } = {}) => {
  const awayRef = useRef(false);
  const handlers = useRef({ onAway, onBack });
  handlers.current = { onAway, onBack };
  useEffect(() => {
    if (!active) return undefined;
    const present = () => !document.hidden && document.hasFocus();
    const away = () => {
      if (awayRef.current) return;
      awayRef.current = true;
      if (handlers.current.onAway) handlers.current.onAway();
    };
    const back = () => {
      if (!awayRef.current || !present()) return;
      awayRef.current = false;
      if (handlers.current.onBack) handlers.current.onBack();
    };
    const onVisibility = () => (document.hidden ? away() : back());
    window.addEventListener('blur', away);
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', away);
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active]);
  return awayRef;
};

export const isAnswered = (answers, q) => Array.isArray(answers[q.id]) && answers[q.id].length > 0;

/** Pick an option: single replaces, multiple toggles. Returns the new answers. */
export const pickAnswer = (answers, q, key) => {
  const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
  const next = q.type === 'single'
    ? [key]
    : (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  return { ...answers, [q.id]: next };
};

/** One question on screen, with progress and Previous / Next / Review. */
export const QuestionStep = ({ questions, index, answers, onPick, onIndex, onReview }) => {
  const current = questions[index];
  if (!current) return null;
  const answeredCount = questions.filter((q) => isAnswered(answers, q)).length;
  return (
    <>
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
        <span>Question {index + 1} of {questions.length}</span>
        <span>{answeredCount} answered</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-5">
        <div className="h-full bg-blue-600 transition-all" style={{ width: `${((index + 1) / questions.length) * 100}%` }} />
      </div>

      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">
        {current.type === 'single' ? 'Choose one' : 'Choose all that apply'}
        <span className="ml-2 normal-case tracking-normal text-slate-400">· {current.points} point{current.points === 1 ? '' : 's'}</span>
        {current.mustPass && (
          <span className="ml-2 normal-case tracking-normal px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-semibold">
            Must answer correctly
          </span>
        )}
      </p>
      <p className="text-base font-medium text-slate-900 whitespace-pre-line mb-4">{current.text}</p>

      <div className="space-y-2">
        {current.options.map((o) => {
          const on = (answers[current.id] || []).includes(o.key);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onPick(current, o.key)}
              className={`w-full text-left flex items-start gap-3 px-3.5 py-3 rounded-lg border transition-colors ${
                on ? 'bg-blue-50 border-blue-500 text-slate-900' : 'bg-white border-slate-300 text-slate-700 hover:border-blue-400'}`}
            >
              <span className={`mt-0.5 shrink-0 w-5 h-5 border-2 flex items-center justify-center ${
                current.type === 'single' ? 'rounded-full' : 'rounded'} ${on ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                {on && <span className={`bg-white ${current.type === 'single' ? 'w-2 h-2 rounded-full' : 'w-2.5 h-2.5 rounded-sm'}`} />}
              </span>
              <span className="text-sm whitespace-pre-line">{o.text}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 mt-6">
        <button
          type="button"
          onClick={() => onIndex(Math.max(0, index - 1))}
          disabled={index === 0}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        {index < questions.length - 1 ? (
          <button
            type="button"
            onClick={() => onIndex(Math.min(questions.length - 1, index + 1))}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onReview}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            <Flag className="w-4 h-4" /> Review & Submit
          </button>
        )}
      </div>
    </>
  );
};

/** Which questions are answered; open one, go back, or submit. */
export const ReviewStep = ({ questions, answers, onOpen, onSubmit, submitLabel = 'Submit Quiz', busy = false }) => {
  const unanswered = questions.filter((q) => !isAnswered(answers, q)).length;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700">
        {unanswered === 0
          ? `All ${questions.length} questions answered. Submit when you are ready — this sitting closes once submitted.`
          : `${unanswered} of ${questions.length} question${questions.length === 1 ? '' : 's'} still unanswered. Open one to answer it, or submit as is — unanswered questions score nothing.`}
      </p>
      <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
        {questions.map((q, i) => (
          <button
            key={q.id}
            type="button"
            onClick={() => onOpen(i)}
            className={`py-2 rounded-lg border text-sm font-semibold tabular-nums transition-colors ${
              isAnswered(answers, q)
                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'}`}
            title={isAnswered(answers, q) ? 'Answered' : 'Not answered'}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-3">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-300" /> answered</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-300" /> not answered</span>
      </p>
      <div className="flex items-center justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={() => onOpen(questions.length - 1)}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Questions
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

/** The submit confirmation, same words on both surfaces. */
export const ConfirmSubmitModal = ({ unanswered, busy = false, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div className="p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">Submit Your Answers?</h3>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-slate-700">
          {unanswered > 0
            ? `${unanswered} question${unanswered === 1 ? ' is' : 's are'} still unanswered and will score nothing. `
            : ''}
          Once submitted this sitting is closed and graded.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Go Back</button>
          <button onClick={onConfirm} disabled={busy} className="px-3 py-2 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50">
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

/** "Before You Start" — the rules the participant reads on the intro. */
export const SittingRules = ({ passMark, mustPassCount, idleExpiryMinutes }) => (
  <div className="rounded-lg border border-slate-200 p-4">
    <p className="text-sm font-semibold text-slate-900 mb-2">Before You Start</p>
    <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-5">
      <li><span className="font-medium text-slate-800">The clock runs from Start to Submit</span> — the total time is recorded for the L&amp;D team.</li>
      <li>Questions come <span className="font-medium text-slate-800">one at a time</span>. You can go back to earlier questions and change an answer before you submit.</li>
      <li>Your answers are saved as you go — if the page reloads, you resume with the same clock.</li>
      {idleExpiryMinutes != null && (
        <li>
          <span className="font-medium text-slate-800">Finish in one go.</span> A sitting left for more than {idleExpiryMinutes} minutes
          expires: it counts as an attempt and the next one starts over with new answers.
        </li>
      )}
      <li><span className="font-medium text-slate-800">Answer on your own.</span> Copying is disabled, and every time you leave this tab it is recorded next to your result.</li>
      <li>Multiple-choice questions score only when every correct option is picked.</li>
      {(passMark != null || mustPassCount > 0) && (
        <li><span className="font-medium text-slate-800">Sit until you pass.</span> If a sitting ends Not passed you can retake the quiz straight away; the number of attempts is recorded.</li>
      )}
      {mustPassCount > 0 && (
        <li>
          <span className="font-medium text-amber-800">
            {mustPassCount} question{mustPassCount === 1 ? ' is' : 's are'} marked “Must answer correctly”
          </span>
          {' '}— getting one wrong means Not passed, whatever your overall score. They are labelled on screen.
        </li>
      )}
    </ul>
  </div>
);

/**
 * The paper's facts on the intro and result screens. `session` is optional —
 * a preview of a bank paper has no session behind it.
 */
export const QuizFacts = ({ session, quiz }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-1.5 text-sm">
    {session && (
      <>
        <p className="font-medium text-slate-800">{session.courseTitle}</p>
        <p className="text-slate-500">
          {formatDate(session.date)}
          {session.startEndTime ? ` · ${session.startEndTime}` : ''}
          {session.trainerNames ? ` · Trainer: ${session.trainerNames}` : ''}
        </p>
      </>
    )}
    <p className={`text-slate-700 ${session ? 'pt-1' : ''}`}>
      <span className="font-medium">{quiz.title}</span>
      <span className="text-slate-500"> · {quiz.questionCount} question{quiz.questionCount === 1 ? '' : 's'} · {quiz.maxScore} point{quiz.maxScore === 1 ? '' : 's'}</span>
      {quiz.passMark != null && <span className="text-slate-500"> · pass mark {quiz.passMark}%</span>}
      {quiz.mustPassCount > 0 && (
        <span className="text-amber-700"> · {quiz.mustPassCount} must-answer question{quiz.mustPassCount === 1 ? '' : 's'}</span>
      )}
    </p>
    {quiz.description && <p className="text-slate-600 whitespace-pre-line">{quiz.description}</p>}
  </div>
);

/**
 * The graded sitting. Green once the quiz is done; amber for Not passed,
 * where the next step is Retake. No time taken: the sitting time is recorded
 * but shown only to the L&D side.
 */
export const QuizResultCard = ({ result, onRetake, busy = false }) => {
  const failed = result.passed === false;
  const tone = failed
    ? { box: 'bg-amber-50 border-amber-200', tile: 'border-amber-100', head: 'text-amber-800' }
    : { box: 'bg-green-50 border-green-200', tile: 'border-green-100', head: 'text-green-800' };
  return (
    <div className={`p-5 border rounded-lg space-y-3 ${tone.box}`}>
      <p className={`flex items-center gap-2 font-medium ${tone.head}`}>
        {failed ? 'Not passed — you can sit the quiz again.' : 'Your quiz has been submitted.'}
        {result.attemptNo > 1 && <span className="text-xs font-normal text-slate-500">(attempt {result.attemptNo})</span>}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className={`bg-white rounded-lg border px-3 py-2 ${tone.tile}`}>
          <p className="text-xs text-slate-500">Score</p>
          <p className="text-xl font-bold text-slate-900 tabular-nums">{result.score} <span className="text-sm font-medium text-slate-500">/ {result.maxScore}</span></p>
        </div>
        <div className={`bg-white rounded-lg border px-3 py-2 ${tone.tile}`}>
          <p className="text-xs text-slate-500">Percentage</p>
          <p className="text-xl font-bold text-slate-900 tabular-nums">{fmtPercent(result.percent)}</p>
        </div>
        <div className={`bg-white rounded-lg border px-3 py-2 ${tone.tile}`}>
          <p className="text-xs text-slate-500">Result</p>
          <p className="mt-1">{result.passed == null ? <span className="text-sm text-slate-500">Score only</span> : <QuizVerdictBadge passed={result.passed} />}</p>
        </div>
      </div>
      {result.correctCount != null && (
        <p className="text-xs text-slate-600">
          {result.correctCount} of {result.questionCount} question{result.questionCount === 1 ? '' : 's'} answered correctly
          {result.submittedAt ? ` · submitted ${formatDateTime(result.submittedAt)}` : ''}.
        </p>
      )}
      {/* A fail that a passing percentage cannot explain: name the rule. */}
      {failed && result.criticalMissed > 0 && (
        <p className="text-xs text-amber-800">
          {result.criticalMissed} “must answer correctly” question{result.criticalMissed === 1 ? ' was' : 's were'} missed
          — those must all be right to pass, whatever the overall score.
        </p>
      )}
      {failed && onRetake && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-slate-600">The quiz stays on your Overview until you pass. Every attempt is recorded.</p>
          <button
            type="button"
            onClick={onRetake}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" /> {busy ? 'Opening…' : 'Retake Quiz'}
          </button>
        </div>
      )}
    </div>
  );
};
